import { ReactElement } from 'react';
import { Link } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import { KeyboardBackspaceRounded } from '@mui/icons-material';
import classNames from 'classnames';
import { Field, FormikProvider, useFormik } from 'formik';

import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import InputBlock from 'components/global/inputBlock/inputBlock';
import { AppState } from 'store';
import { downloadLearnerMatchingMetrics } from 'store/learnerMatchingMetrics/actions';
import { isDownloadingLearnerMatchingMetricsSelector } from 'store/learnerMatchingMetrics/selectors';
import { buildLearnerMatchingMetricsSchema } from './validationSchema';

import styles from './LearnerMatchingMetrics.module.scss';

interface IFormModel {
  startDate: Date;
  endDate: Date;
}

export const LearnerMatchingMetricsPage = (): ReactElement => {
  const dispatch: ThunkDispatch<AppState, unknown, AnyAction> = useDispatch();
  const isDownloading = useSelector(isDownloadingLearnerMatchingMetricsSelector);

  const formik = useFormik<IFormModel>({
    enableReinitialize: true,
    initialValues: {
      startDate: undefined,
      endDate: undefined,
    },
    onSubmit: (values: IFormModel) => {
      dispatch(
        downloadLearnerMatchingMetrics({
          startDate: values.startDate,
          endDate: values.endDate,
        }),
      );
    },
    validationSchema: buildLearnerMatchingMetricsSchema(),
  });

  const { errors, touched, isValid, dirty, handleBlur } = formik;

  return (
    <>
      <div className="form-container">
        <div className="page-navigation">
          <Link className="tertiary" to="/admin">
            <KeyboardBackspaceRounded className="tertiary-icon-back" />
            Admin
          </Link>
        </div>
        <div className="form-title-container">
          <div className={classNames('title', styles.title)}>
            <h4>Learner Matching Metrics</h4>
          </div>
        </div>
        <div className={styles.formContainer}>
          <FormikProvider value={formik}>
            <div className={styles.formFieldGrid}>
              <div>
                <InputBlock errors={errors} name="startDate" touched={touched} title="Start Date (mm/dd/yyyy)">
                  <Field label="Start Date" name="startDate">
                    {({ field }) => (
                      <DatePicker onBlur={handleBlur} field={field} formikKey="startDate" timeOfDay="midnight" />
                    )}
                  </Field>
                </InputBlock>
              </div>
              <div>
                <InputBlock errors={errors} name="endDate" touched={touched} title="End Date (mm/dd/yyyy)">
                  <Field label="End Date" name="endDate">
                    {({ field }) => (
                      <DatePicker onBlur={handleBlur} field={field} formikKey="endDate" timeOfDay="endOfDay" />
                    )}
                  </Field>
                </InputBlock>
              </div>
            </div>
            <div className={styles.actions}>
              <Button
                disabled={!dirty || !isValid || isDownloading}
                className="primary"
                onClick={() => formik.handleSubmit()}
              >
                Download
              </Button>
            </div>
          </FormikProvider>
        </div>
      </div>
    </>
  );
};
